import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core';
import {
  ArrowBack,
  Close,
  FileCopy,
  InsertComment,
  LibraryBooks,
  // PieChart,
} from '@material-ui/icons';

import {
  NavDrawer,
  NavDrawerList,
  NavDrawerListItem, // This component is a source of console spam, but comes from a bug in redshelf-ui:
  Typography,
  // Material-UI: The key `label` provided to the classes prop is not implemented in ForwardRef(Button).

  // NavDrawerTitle, - this has been temporarily removed/replaced for Phase 1, work in rs-ui to resolve differences between rs-ui and reader NavDrawerTitle component will be completed in RP-4441
} from '@redshelf/redshelf-ui';

import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  IconButton,
  NotesHighlightsMenu,
  FlashcardsMenu,
  Logo,
  CopiedContentMenu,
} from 'components';

import { ViewportValuesContext } from 'contexts';
import { BRANDING_SOURCE } from './brandingSource';

const useStyles = makeStyles((theme) => {
  return {
    navDrawer: {
      '& .MuiDesktopNavDrawer-scrollContainer': {
        overflow: 'hidden',
      },
      '&.MuiDesktopNavDrawer-rootDense + .MuiDesktopNavDrawer-root .MuiDesktopNavDrawer-paper':
        {
          height: (props) =>
            theme.breakpoints.down('xs')
              ? `calc(100% - ${props.footerHeight}px)`
              : '100%',
        },
      width: theme.spacing(32),
      '& .MuiDrawer-paper': {},
      '& .MuiNavDrawerTitle-root': {
        marginBottom: 0,
      },

      '& .MuiNavDrawerList-root': {
        paddingTop: theme.spacing(0.75),
      },
      '& nav': {
        padding: 0,
        [theme.breakpoints.up('sm')]: {
          paddingTop: ({ alertBannerHeight }) => `${alertBannerHeight}px`,
        },
      },
      '& .MuiToolbar-root': {
        minHeight: theme.spacing(8),
        height: theme.spacing(8),
      },
    },
    navDrawerContainer: {
      position: 'relative',
      zIndex: 1,
      [theme.breakpoints.down('xs')]: {
        zIndex: 3,
      },
    },
    navDrawerList: {
      maxHeight: (props) => `calc(100vh - ${props.footerHeight}px - 65px)`, // 65px = topbar
      overflowY: 'auto',
    },
    title: {
      ...theme.typography.h6,
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      color: theme.palette.greyBlue[700],
      flex: 1,
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: theme.spacing(1.875), // 15px
      paddingBottom: theme.spacing(1.875), // 15px
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(0),
      border: `1px solid ${theme.palette.divider}`,
      borderRight: 0,
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      position: 'fixed',
      zIndex: 100,
      [theme.breakpoints.up('sm')]: {
        top: ({ alertBannerHeight }) => `${alertBannerHeight + 64}px`, // alertBannerHeight + height of topbar
      },
      background: theme.palette.common.white,
      width: '255px',
    },
    titleContainerUnderlay: {
      position: 'fixed',
      zIndex: 99,
      [theme.breakpoints.up('sm')]: {
        top: ({ alertBannerHeight }) => `${alertBannerHeight + 64}px`, // alertBannerHeight + height of topbar
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      background: theme.palette.common.white,
      width: '256px',
      height: '76px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleContainerSpacer: {
      position: 'relative',
      zIndex: 99,
      background: theme.palette.common.white,
      height: '76px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    arrowBack: {
      paddingRight: '16px',
    },
    mobileScrollableContainer: {
      overflowY: 'auto',
      position: 'absolute',
      top: '76px',
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    mobileBranding: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'end',
    },
    mobileBrandingRibbon: {
      width: '100%',
      marginTop: '20px',
    },
    mobileBrandingPoweredBy: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '16px',
      marginTop: '2px',
    },
    mobileBrandingPoweredByText: {
      fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
      fontWeight: '500',
      fontStyle: 'normal',
      fontSize: '14px',
      lineHeight: '21px',
      color: theme.palette.greyBlue[600],
      marginRight: '0.3em',
    },
    closeButton: {
      minHeight: theme.spacing(5.5),
      minWidth: theme.spacing(5.5),
    },
  };
});

const NavDrawerListItemRSUI = withStyles((theme) => ({
  button: {
    minHeight: theme.spacing(9),

    '&:hover': {
      color: theme.palette.shelfBlue[500],
      backgroundColor: 'transparent',
      border: '1px solid transparent',
      borderRadius: 'unset',
      borderLeft: `2px solid ${theme.palette.shelfBlue[500]}`,
    },
    '&:focus': {
      color: theme.palette.shelfBlue[500],
      backgroundColor: 'transparent',
      border: '1px solid transparent',
      borderRadius: 'unset',
      borderLeft: `2px solid ${theme.palette.shelfBlue[500]}`,
    },

    [theme.breakpoints.down('xs')]: {
      minHeight: theme.spacing(5.5),
    },
  },
  root: {
    '& .Mui-focusVisible': {
      borderLeft: `4px solid ${theme.palette.shelfBlue[500]}`,
      outlineOffset: '-2px',
    },
  },
}))(NavDrawerListItem);

const NavDrawerTitle = ({
  classNames,
  closeButtonRef,
  onClose,
  onCollapse,
  title,
  id,
}) => {
  const classes = useStyles();

  return (
    <>
      <div className={classNames.titleContainer}>
        {onCollapse && (
          <IconButton
            aria-label="close all"
            onClick={onCollapse}
            size="small"
            edge="start"
            className={classNames.arrowBack}
          >
            <ArrowBack />
          </IconButton>
        )}
        <Typography component="h2" className={classNames.title} id={id}>
          {title}
        </Typography>
        <IconButton
          ref={closeButtonRef}
          className={classes.closeButton}
          aria-label="close"
          onClick={onClose}
          size="small"
          edge="start"
        >
          <Close />
        </IconButton>
      </div>
      <div className={classes.titleContainerUnderlay}></div>
      <div className={classes.titleContainerSpacer}></div>
    </>
  );
};

const SideNav = ({
  alertBannerHeight,
  closeLearningTools,
  currentMenuChanged = () => {},
  footerHeight,
  isOpenOnDesktop,
  isOpenOnPhoneAndTablet,
  notesMenuOptionRef,
  ...props
}) => {
  const classes = useStyles({ alertBannerHeight, footerHeight });

  const { viewport } = useContext(ViewportValuesContext);
  const isSmallerThanPhoneWidth =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.PHONE;
  const isSmallerThanTabletWidth =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const isOpen = isSmallerThanTabletWidth
    ? isOpenOnPhoneAndTablet
    : isOpenOnDesktop;

  const [menuName, setMenuName] = useState(null);
  const [isNestedNavOpen, setIsNestedNavOpen] = useState(false);
  const [lastActiveElement, setLastActiveElement] = useState();

  const flashcardsMenuOptionRef = useRef();
  const copiedContentMenuOptionRef = useRef();
  const closeButtonRef = useRef(null);

  // set focus on open Learning Tools menu drawer close button
  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isNestedNavOpen, menuName]);

  const toggleNestedNav = () => {
    setIsNestedNavOpen((prev) => {
      return !prev;
    });
  };
  const drawerClickListener = (text) => {
    if (!isNestedNavOpen) {
      toggleNestedNav();
    }
    currentMenuChanged(text);
    return setMenuName(text);
  };
  const handleNestedOnCloseClick = () => {
    currentMenuChanged(null);
    setMenuName(null);
    setIsNestedNavOpen(false);

    if (!isSmallerThanPhoneWidth) {
      // send focus back to last focused LT menu item so it is not lost
      lastActiveElement.focus();
    }
  };
  const handleNestedOnCloseAllClick = () => {
    handleNestedOnCloseClick();
    closeLearningTools();
  };

  const navDrawerList = (
    <NavDrawerList className={classes.navDrawerList}>
      <NavDrawerListItemRSUI
        aria-controls="nested-nav-drawer"
        aria-expanded={menuName === 'Notes & Highlights'}
        ref={notesMenuOptionRef}
        onClick={() => drawerClickListener('Notes & Highlights')}
        onFocus={() => {
          if (!isSmallerThanPhoneWidth) {
            setLastActiveElement(notesMenuOptionRef.current.firstChild);
          }
        }}
        icon={<InsertComment />}
        label={'Notes & Highlights'}
        key={0}
      />
      <NavDrawerListItemRSUI
        aria-controls="nested-nav-drawer"
        aria-expanded={menuName === 'Flashcards'}
        ref={flashcardsMenuOptionRef}
        onClick={() => drawerClickListener('Flashcards')}
        onFocus={() => {
          if (!isSmallerThanPhoneWidth) {
            setLastActiveElement(flashcardsMenuOptionRef.current.firstChild);
          }
        }}
        icon={<LibraryBooks />}
        label={'Flashcards'}
        key={1}
      />
      <NavDrawerListItemRSUI
        aria-controls="nested-nav-drawer"
        aria-expanded={menuName === 'Copied Content'}
        ref={copiedContentMenuOptionRef}
        onClick={() => drawerClickListener('Copied Content')}
        onFocus={() => {
          if (!isSmallerThanPhoneWidth) {
            setLastActiveElement(copiedContentMenuOptionRef.current.firstChild);
          }
        }}
        icon={<FileCopy />}
        label={'Copied Content'}
        key={3}
      />
    </NavDrawerList>
  );

  const handleEscOnNestedDrawer = (e) => {
    if (e.key === 'Escape' && isOpen && isNestedNavOpen) {
      !isSmallerThanPhoneWidth
        ? handleNestedOnCloseClick()
        : handleNestedOnCloseAllClick();
    }
  };

  const handleEscOnNavDrawer = (e) => {
    if (e.key === 'Escape' && isOpen && isSmallerThanPhoneWidth) {
      handleNestedOnCloseAllClick();
    }
  };

  // rsui doesn't allow passing more props to PaperPros
  // so overwriting the entire prop
  const paperPropsOverwrite = {
    PaperProps: {
      'aria-labelledby': 'nav-drawer-name',
      component: 'nav',
      id: 'nested-nav-drawer',
    },
  };

  return (
    <div className={classes.navDrawerContainer} {...props}>
      <NavDrawer
        id="nav-drawer"
        aria-label="Navigation drawer"
        breakpoint="xs"
        className={classes.navDrawer}
        desktopProps={{
          includeTopBarOffset: true,
          isOpen: isSmallerThanTabletWidth
            ? isOpenOnPhoneAndTablet
            : isOpenOnDesktop,
        }}
        mobileProps={{ isOpen: isOpenOnPhoneAndTablet }}
        isDense={isSmallerThanPhoneWidth ? false : true}
        onKeyUp={handleEscOnNavDrawer}
      >
        {isSmallerThanPhoneWidth && (
          <NavDrawerTitle
            classNames={classes}
            onClose={closeLearningTools}
            title="Learning Tools"
          />
        )}
        {!isSmallerThanPhoneWidth ? (
          navDrawerList
        ) : (
          <div className={classes.mobileScrollableContainer}>
            {navDrawerList}
            <div className={classes.mobileBranding}>
              <img
                className={classes.mobileBrandingRibbon}
                alt=""
                src={BRANDING_SOURCE}
              ></img>
              <div className={classes.mobileBrandingPoweredBy}>
                <div className={classes.mobileBrandingPoweredByText}>
                  Powered by
                </div>
                <Logo height="26" />
              </div>
            </div>
          </div>
        )}
      </NavDrawer>
      <NavDrawer
        aria-label="Nested navigation drawer"
        breakpoint="xs"
        className={classes.navDrawer}
        onKeyUp={handleEscOnNestedDrawer}
        desktopProps={{
          includeTopBarOffset: true,
          isOpen: isOpen && isNestedNavOpen,
          ...paperPropsOverwrite,
        }}
        mobileProps={{
          isOpen: isOpen && isNestedNavOpen,
          ...paperPropsOverwrite,
        }}
      >
        <>
          {menuName && (
            <>
              {isSmallerThanPhoneWidth ? (
                <NavDrawerTitle
                  classNames={classes}
                  closeButtonRef={closeButtonRef}
                  onClose={() => {
                    handleNestedOnCloseAllClick();
                  }}
                  id="nav-drawer-name"
                  title={menuName}
                  onCollapse={handleNestedOnCloseClick}
                />
              ) : (
                <NavDrawerTitle
                  classNames={classes}
                  closeButtonRef={closeButtonRef}
                  id="nav-drawer-name"
                  onClose={() => {
                    handleNestedOnCloseClick();
                  }}
                  title={menuName}
                />
              )}
              {(() => {
                switch (menuName) {
                  case 'Notes & Highlights':
                    return <NotesHighlightsMenu />;
                  case 'Flashcards':
                    return <FlashcardsMenu />;
                  case 'Copied Content':
                    return <CopiedContentMenu />;
                  // case 'readingActivity':
                  default:
                    return <NavDrawerListItem label="Placeholder Item" />;
                }
              })()}
            </>
          )}
        </>
      </NavDrawer>
    </div>
  );
};

SideNav.propTypes = {
  alertBannerHeight: PropTypes.number,
  closeLearningTools: PropTypes.func,
  currentMenuChanged: PropTypes.func,
  footerHeight: PropTypes.number,
  isOpenOnDesktop: PropTypes.bool,
  isOpenOnPhoneAndTablet: PropTypes.bool,
  notesMenuOptionRef: PropTypes.object,
};

export default memo(SideNav);
